import { LitElement, html, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { onThemeChange } from '@vitesse/base-ui/theme';
import { registerChartFrame } from './chart-clock';
import { CHART_THEMES, formatValue, type ThemeOption } from './chart-types';
import { t } from '@vitesse/base-ui/i18n';
import {
  calculateTimelineXTicks,
  calculateTimelineYScale,
  clampBlindRanges,
  formatCrosshairTime,
  lowerBound,
  panViewport,
  segmentBuckets,
  timeToX,
  xToTime,
  zoomViewport,
  type TimelineViewport,
  type TimelineYScale,
} from './timeline-types';
import type { TimelineData } from '../controller/history-viewport-controller';
import { ChartHoverTooltip, type HoverReadoutRow } from './hover-tooltip';

/*
 * <vitesse-timeline-chart> — the History tab's explorable timeline. CANVAS, not
 * SVG like <vitesse-chart>: during a drag every rAF frame redraws the whole
 * scene anyway (the live chart's static-SVG trick only pays when most frames
 * DON'T move), and honest gap-breaking is canvas's native moveTo/lineTo idiom.
 *
 * CONTROLLED component: it renders the {viewport, lanes} it is given and emits
 * gesture intents as `viewport-change` events — it never fetches and never
 * moves the window by itself, so everything on screen traces to a user action.
 *
 * LANES: up to a few symbols stack as equal-height strips over ONE time axis —
 * the Perfetto shape. All lanes live in this single canvas on purpose: one
 * wheel/pointer handler and one rAF means zoom/pan synchronization across
 * lanes is structural, not something achieved by coordinating siblings. Each
 * lane has its own y-scale (symbols carry no units, so a shared axis would
 * flatten a boolean beside a big counter); the x-axis, gridline verticals and
 * crosshair span all lanes. With ONE lane the render is identical to the
 * original single-series chart.
 *
 * Three visual states per x-range, deliberately distinct ("absent is not
 * zero"): data (band/line), CONFIRMED gap (plain background — the historian
 * answered and there was nothing), and NOT-YET-FETCHED (hatched veil — we
 * haven't asked; it must not read as "no data").
 */

/** One stacked strip: a symbol's render snapshot plus its slot color. */
export interface TimelineLane {
  /** Stable identity — keys the held y-scale and labels the strip. */
  symbolPath: string;
  data: TimelineData | null;
  theme: ThemeOption;
}

/** One lane's axis for this frame — the scale AND the exact y-mapping closure
 *  used to draw it, so the crosshair can read the identical axis. */
interface LaneScale {
  scale: TimelineYScale;
  toY: (val: number) => number;
}

const MARGIN = { top: 12, right: 14, bottom: 30, left: 52 };
/** Vertical gap between stacked lanes (CSS px). */
const LANE_GAP = 10;
/** Below this lane height, halve the y ticks so labels don't crowd. */
const DENSE_TICKS_BELOW_PX = 140;

/** One wheel "line" (Firefox deltaMode 1) ≈ this many pixels. */
const LINE_PX = 33;
/** A silent gap this long ends a wheel gesture (next event re-classifies). */
const WHEEL_GESTURE_RESET_MS = 150;

type WheelKind = 'pan' | 'zoom';

@customElement('vitesse-timeline-chart')
export class VitesseTimelineChart extends LitElement {
  @property({ attribute: false }) viewport: TimelineViewport = { startMs: Date.now() - 60_000, endMs: Date.now() };
  @property({ attribute: false }) lanes: TimelineLane[] = [];
  /** Newest instant a gesture may reach (the owner's frozen tab-open clock).
   *  The view is retrospective: without this pinned, zoom-out/pan-right clamps
   *  would read live Date.now() and quietly slide the window into the present. */
  @property({ type: Number }) maxEndMs: number | null = null;
  /** Earliest recorded instant for the device (null = unknown/no data). The
   *  region before it is KNOWN empty, drawn as a dim pre-data wash with a
   *  dashed "data begins" boundary — a fourth visual state, distinct from the
   *  loading veil (hatched) and a confirmed gap (plain background). */
  @property({ type: Number }) dataStartMs: number | null = null;

  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private ro?: ResizeObserver;
  private unClock?: () => void;
  private unTheme?: () => void;
  private w = 0;
  private h = 0;
  /** What the last paint drew, for the per-frame dirty check: the viewport
   *  and each lane's data version. Everything else that can change the scene
   *  (size, hover, theme, lane set, dataStartMs) sets `forceDirty` at the
   *  point of change, so a frame at rest costs a handful of integer compares —
   *  the old per-rAF signature STRING was built and compared 60×/s even idle. */
  private readonly lastDrawn = { startMs: NaN, endMs: NaN, versions: [] as number[] };
  private forceDirty = true;
  /** Grid/label colors, read from CSS custom properties. Cached: resolving
   *  computed style forces a style recalc, and it used to run on every paint —
   *  every frame of a drag. The values only move on theme change and layout. */
  private readonly colors = { border: 'rgba(128,128,128,0.3)', label: '#888' };
  /** Held y-scale PER LANE (keyed by symbolPath) — a blind stretch holds the
   *  lane's own previous axis, never a neighbour's. */
  private readonly lastScales = new Map<string, TimelineYScale>();

  // Hover / drag state
  private hoverX: number | null = null; // plot-local CSS px
  private hoverClient = { x: 0, y: 0 };
  private canvasRect: DOMRect | null = null;
  private dragging = false;
  private dragLastX = 0;
  private readonly tooltip = new ChartHoverTooltip();

  // Wheel gesture classification (held for the whole gesture — momentum events
  // must not flip a pan into a zoom halfway through a flick).
  private wheelKind: WheelKind | null = null;
  private wheelLastTs = 0;

  // rAF-coalesced gesture state. Wheel/drag events arrive at 60–120Hz — well
  // above display rate — and a per-event `viewport-change` synchronously ran
  // the whole section → per-lane data-plane stack each time. Instead, events
  // accumulate into `gestureViewport` (drawn from directly, so visuals never
  // lag) and at most ONE event per frame is dispatched from frame(). The
  // parent echoes the property back; when it catches up (or overrides — a
  // preset click), willUpdate hands control back to the property.
  private gestureViewport: TimelineViewport | null = null;
  /** The last gesture value actually dispatched (null = nothing pending). */
  private emittedViewport: TimelineViewport | null = null;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.ro = new ResizeObserver(() => this.measure());
    this.ro.observe(this);
    this.unClock = registerChartFrame(() => this.frame());
    // The grid/label colors are canvas PIXELS, not live CSS — a theme flip
    // leaves the old theme's strokes on screen until the next dirty frame
    // (dark #2d2d2d reads as black on the new light background). Repaint
    // synchronously inside the theme listener: it fires within the View
    // Transition's update callback, so the "new" snapshot the crossfade
    // animates to already carries the new theme's grid.
    this.unTheme = onThemeChange(() => {
      this.readColors();
      this.forceDirty = true;
      this.frame();
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.ro?.disconnect();
    this.unClock?.();
    this.unTheme?.();
    this.tooltip.dispose();
    this.canvas = null;
    this.ctx = null;
  }

  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('viewport') && this.gestureViewport) {
      const eq = (a: TimelineViewport | null, b: TimelineViewport | null): boolean =>
        !!a && !!b && a.startMs === b.startMs && a.endMs === b.endMs;
      if (eq(this.viewport, this.gestureViewport)) {
        // The parent's echo caught up with the newest gesture state.
        this.gestureViewport = null;
        this.emittedViewport = null;
      } else if (eq(this.gestureViewport, this.emittedViewport)) {
        // Nothing newer pending, yet the parent sent a DIFFERENT viewport — a
        // programmatic change (preset/custom range) or a clamp disagreement.
        // The property is authoritative; adopt it.
        this.gestureViewport = null;
        this.emittedViewport = null;
      }
      // Otherwise: mid-gesture, newer local state exists — keep drawing ours;
      // frame() will emit it and the echo will converge.
    }
    if (changed.has('lanes')) {
      // Drop held scales for lanes that left — a symbol removed and re-added
      // must rescale from its data, not resurrect a stale axis.
      const live = new Set(this.lanes.map((l) => l.symbolPath));
      for (const key of this.lastScales.keys()) if (!live.has(key)) this.lastScales.delete(key);
      // The parent rebuilds the array every render, so identity means nothing;
      // repaint only when the lane SET or a slot color actually changed (data
      // versions are checked per frame — see frame()).
      const prev = changed.get('lanes') as TimelineLane[] | undefined;
      if (
        !prev ||
        prev.length !== this.lanes.length ||
        this.lanes.some((l, i) => l.symbolPath !== prev[i].symbolPath || l.theme !== prev[i].theme)
      ) {
        this.forceDirty = true;
      }
    }
    if (changed.has('dataStartMs')) this.forceDirty = true;
  }

  protected firstUpdated(): void {
    this.canvas = this.querySelector('canvas');
    this.ctx = this.canvas?.getContext('2d') ?? null;
    // Explicitly non-passive: preventDefault must actually stop the page from
    // scrolling/zooming (some engines default wheel listeners to passive).
    this.canvas?.addEventListener('wheel', this.onWheel, { passive: false });
    this.measure();
  }

  get plotWidth(): number {
    return Math.max(0, this.w - MARGIN.left - MARGIN.right);
  }
  private get plotHeight(): number {
    return Math.max(0, this.h - MARGIN.top - MARGIN.bottom);
  }

  private readColors(): void {
    const cs = getComputedStyle(this);
    this.colors.border = cs.getPropertyValue('--border-color').trim() || 'rgba(128,128,128,0.3)';
    this.colors.label = cs.getPropertyValue('--text-tertiary').trim() || '#888';
  }

  private measure(): void {
    // Layout is the other moment the palette can have changed under us (a
    // stylesheet landing, a container re-theme) — resize callbacks are rare,
    // so re-reading here is free and keeps the cache honest.
    this.readColors();
    const r = this.getBoundingClientRect();
    const w = Math.floor(r.width);
    const h = Math.floor(r.height);
    if (w === this.w && h === this.h) return;
    this.w = w;
    this.h = h;
    if (this.canvas) {
      const dpr = window.devicePixelRatio || 1;
      this.canvas.width = Math.max(1, Math.round(w * dpr));
      this.canvas.height = Math.max(1, Math.round(h * dpr));
      this.canvas.style.width = `${w}px`;
      this.canvas.style.height = `${h}px`;
    }
    this.forceDirty = true;
    this.dispatchEvent(new CustomEvent('plot-resize', { detail: { width: this.plotWidth }, bubbles: true, composed: true }));
  }

  // ── gestures ───────────────────────────────────────────────────────────────

  private commit(v: TimelineViewport): void {
    this.dispatchEvent(new CustomEvent('viewport-change', { detail: v, bubbles: true, composed: true }));
  }

  /** The viewport gestures compute from and the canvas draws: local gesture
   *  state while one is in progress, the controlled property otherwise. */
  private effectiveViewport(): TimelineViewport {
    return this.gestureViewport ?? this.viewport;
  }

  /** Accumulate a gesture step locally; frame() dispatches at most one
   *  `viewport-change` per rAF and draws this value directly. */
  private applyGesture(v: TimelineViewport): void {
    this.gestureViewport = v;
    this.forceDirty = true;
  }

  private anchorFrac(clientX: number): number {
    const rect = this.canvasRect ?? this.canvas?.getBoundingClientRect();
    if (!rect || this.plotWidth <= 0) return 0.5;
    return Math.min(1, Math.max(0, (clientX - rect.left - MARGIN.left) / this.plotWidth));
  }

  /**
   * Axis split, decided once per gesture: vertical-dominant scroll zooms at
   * the cursor (maps-style — trackpad two-finger scroll included), while
   * horizontal-dominant scroll pans through time. Mouse wheels only ever emit
   * vertical deltas, so a notch classifies as zoom, same as it always did.
   */
  private classifyWheel(e: WheelEvent): WheelKind {
    return Math.abs(e.deltaX) > Math.abs(e.deltaY) ? 'pan' : 'zoom';
  }

  /** The gesture-time clamp: the frozen anchor when the owner provides one,
   *  live now only as a standalone fallback. */
  private clampNow(): number {
    return this.maxEndMs ?? Date.now();
  }

  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    if (this.plotWidth <= 0) return;
    const now = performance.now();
    const base = this.effectiveViewport();
    if (e.ctrlKey) {
      // Pinch (browsers synthesize ctrlKey for trackpad pinch) or explicit
      // Ctrl+wheel — always zoom, gently (pinch deltas are small + rapid).
      this.applyGesture(zoomViewport(base, Math.exp(-e.deltaY * 0.01), this.anchorFrac(e.clientX), this.clampNow()));
      this.wheelKind = 'zoom';
      this.wheelLastTs = now;
      return;
    }
    if (this.wheelKind === null || now - this.wheelLastTs > WHEEL_GESTURE_RESET_MS) {
      this.wheelKind = this.classifyWheel(e);
    }
    this.wheelLastTs = now;
    const scale = e.deltaMode === WheelEvent.DOM_DELTA_LINE ? LINE_PX : 1;
    if (this.wheelKind === 'zoom') {
      this.applyGesture(zoomViewport(base, Math.exp(-e.deltaY * scale * 0.002), this.anchorFrac(e.clientX), this.clampNow()));
    } else {
      // Horizontal two-finger scroll: ride the OS momentum as-is — each event's delta is
      // applied directly, no added friction (preventDefault does not suppress
      // the browser's synthesized momentum trail; double-decelerating would).
      const d = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      this.applyGesture(panViewport(base, (-d * scale) / this.plotWidth, this.clampNow()));
    }
  };

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    this.dragging = true;
    this.dragLastX = e.clientX;
    this.hoverX = null;
    this.tooltip.hide();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    if (this.canvas) this.canvas.style.cursor = 'grabbing'; // dragging isn't Lit-reactive — cursor set imperatively
    this.forceDirty = true;
  };
  private readonly onPointerMove = (e: PointerEvent): void => {
    this.canvasRect = this.canvas?.getBoundingClientRect() ?? null;
    if (this.dragging) {
      const dx = e.clientX - this.dragLastX;
      this.dragLastX = e.clientX;
      if (dx !== 0 && this.plotWidth > 0) this.applyGesture(panViewport(this.effectiveViewport(), dx / this.plotWidth, this.clampNow()));
      return;
    }
    if (!this.canvasRect) return;
    const x = e.clientX - this.canvasRect.left - MARGIN.left;
    this.hoverX = x >= 0 && x <= this.plotWidth ? x : null;
    this.hoverClient = { x: e.clientX, y: e.clientY };
    this.forceDirty = true;
  };
  private readonly onPointerUp = (): void => {
    this.dragging = false;
    if (this.canvas) this.canvas.style.cursor = '';
    this.forceDirty = true;
  };
  private readonly onPointerLeave = (): void => {
    if (!this.dragging) {
      this.hoverX = null;
      this.tooltip.hide();
      this.forceDirty = true;
    }
  };

  // ── per-frame draw ─────────────────────────────────────────────────────────

  private frame(): void {
    if (!this.ctx || this.plotWidth <= 0 || this.plotHeight <= 0) return;
    // Flush the coalesced gesture: at most one viewport-change per frame,
    // and only when the accumulated value moved since the last dispatch.
    if (
      this.gestureViewport &&
      (!this.emittedViewport ||
        this.gestureViewport.startMs !== this.emittedViewport.startMs ||
        this.gestureViewport.endMs !== this.emittedViewport.endMs)
    ) {
      this.emittedViewport = { ...this.gestureViewport };
      this.commit(this.emittedViewport);
    }
    const v = this.effectiveViewport();
    if (!this.forceDirty && !this.sceneMoved(v)) return;
    this.forceDirty = false;
    this.draw();
    const last = this.lastDrawn;
    last.startMs = v.startMs;
    last.endMs = v.endMs;
    last.versions.length = this.lanes.length;
    for (let i = 0; i < this.lanes.length; i++) last.versions[i] = this.lanes[i].data?.version ?? -1;
  }

  /** Did the viewport or any lane's data change since the last paint? Data
   *  versions are compared here (not in willUpdate) because a lane's snapshot
   *  can bump its version without the parent re-rendering — a throttled fetch
   *  pass trimming or re-picking a fallback — and the old signature caught
   *  that on the next frame; this keeps that guarantee without the string. */
  private sceneMoved(v: TimelineViewport): boolean {
    const last = this.lastDrawn;
    if (v.startMs !== last.startMs || v.endMs !== last.endMs) return true;
    if (this.lanes.length !== last.versions.length) return true;
    for (let i = 0; i < this.lanes.length; i++) {
      if ((this.lanes[i].data?.version ?? -1) !== last.versions[i]) return true;
    }
    return false;
  }

  private draw(): void {
    const ctx = this.ctx!;
    const v = this.effectiveViewport();
    const pw = this.plotWidth;
    const ph = this.plotHeight;
    const dpr = window.devicePixelRatio || 1;
    const cBorder = this.colors.border;
    const cLabel = this.colors.label;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.translate(MARGIN.left, MARGIN.top);
    ctx.font = '10px "SF Mono", ui-monospace, monospace';

    // Lane geometry: equal strips + fixed gaps. Depends ONLY on the lane
    // count — data arriving can never move a lane rect.
    const n = Math.max(1, this.lanes.length);
    const laneH = (ph - LANE_GAP * (n - 1)) / n;
    const laneTop = (i: number): number => i * (laneH + LANE_GAP);

    // Shared x axis: vertical gridlines span every lane; labels drawn once
    // below the whole plot.
    const xticks = calculateTimelineXTicks(v, pw);
    ctx.strokeStyle = cBorder;
    ctx.lineWidth = 1;
    // Span the verticals from the FIRST lane's top gridline to the LAST lane's
    // bottom gridline (matching drawLane's topInset/bottomInset), not the full
    // plot — otherwise they overshoot the bounding horizontal lines by an inset.
    const gTopInset = n > 1 ? 20 : 8;
    const gBottomInset = 8;
    const vTop = laneTop(0) + gTopInset;
    const vBottom = laneTop(n - 1) + laneH - gBottomInset;
    ctx.beginPath();
    for (const tk of xticks) {
      ctx.moveTo(tk.x, vTop);
      ctx.lineTo(tk.x, vBottom);
    }
    // Faint separator through each inter-lane gap's midline.
    for (let i = 1; i < n; i++) {
      const y = laneTop(i) - LANE_GAP / 2;
      ctx.moveTo(0, y);
      ctx.lineTo(pw, y);
    }
    ctx.stroke();
    ctx.fillStyle = cLabel;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (const tk of xticks) {
      ctx.fillText(tk.label, tk.x, ph + 6);
      if (tk.sub) ctx.fillText(tk.sub, tk.x, ph + 17);
    }

    const lanesScale: LaneScale[] = [];
    for (let i = 0; i < this.lanes.length; i++) {
      lanesScale.push(this.drawLane(ctx, this.lanes[i], laneTop(i), laneH, v, pw, cBorder, cLabel, this.lanes.length > 1));
    }

    this.drawDataStartBoundary(ctx, v, pw, ph, cLabel);
    this.drawCrosshair(ctx, v, pw, ph, laneTop, lanesScale, cBorder);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  /** The "data begins" boundary — one dashed vertical spanning every lane (the
   *  bounds are device-wide) plus a dated label, drawn only when the boundary
   *  is actually inside the viewport. Pairs with drawLane's pre-data wash. */
  private drawDataStartBoundary(
    ctx: CanvasRenderingContext2D,
    v: TimelineViewport,
    pw: number,
    ph: number,
    cLabel: string,
  ): void {
    const ds = this.dataStartMs;
    if (ds === null || ds <= v.startMs || ds >= v.endMs) return;
    const x = timeToX(ds, v, pw);
    ctx.save();
    ctx.strokeStyle = cLabel;
    ctx.globalAlpha = 0.6;
    ctx.setLineDash([2, 3]); // distinct from the crosshair's [3,3]
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, ph);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = cLabel;
    ctx.globalAlpha = 1;
    ctx.textBaseline = 'top';
    const label = t('controller.historyDataBegins', { date: formatCrosshairTime(ds, v.endMs - v.startMs) });
    // Label sits in the empty pre-data side when the line is near the right
    // edge; otherwise to the right of the line, clear of the lane labels' row.
    const w = ctx.measureText(label).width;
    if (x + 6 + w > pw) {
      ctx.textAlign = 'right';
      ctx.fillText(label, x - 6, ph - 14);
    } else {
      ctx.textAlign = 'left';
      ctx.fillText(label, x + 6, ph - 14);
    }
    ctx.restore();
  }

  /** One strip: y-scale + gridlines, the not-fetched veil, and the series.
   *  Returns the scale AND the exact y-mapping function used to draw it, so
   *  the crosshair reads values off the identical axis this frame drew with
   *  (rather than re-deriving the inset math in a second place). */
  private drawLane(
    ctx: CanvasRenderingContext2D,
    lane: TimelineLane,
    top: number,
    laneH: number,
    v: TimelineViewport,
    pw: number,
    cBorder: string,
    cLabel: string,
    labelled: boolean,
  ): LaneScale {
    const theme = CHART_THEMES[lane.theme] ?? CHART_THEMES.frost;
    const buckets = lane.data?.buckets ?? [];

    // Vertical breathing room INSIDE the strip: room for the overlaid label at
    // the top (so a peak never draws through the symbol name) and a floor at
    // the bottom (so the lowest tick never sits flush against the next lane's
    // separator — the "squished axis" a tight strip reads as). The data/ticks
    // occupy [topInset, laneH - bottomInset], not the full strip.
    const topInset = labelled ? 20 : 8;
    const bottomInset = 8;
    const usableH = Math.max(1, laneH - topInset - bottomInset);

    // Y scale from VISIBLE buckets only; when the active level has none (rung
    // just crossed), scale from the visible FALLBACK buckets so the axis stays
    // continuous through the crossing; hold the lane's previous scale over a
    // genuinely blind or empty stretch so panning through a gap doesn't snap.
    const fb = lane.data?.fallback ?? null;
    const tickIntervals = laneH < DENSE_TICKS_BELOW_PX ? 2 : 4;
    // The fallback pass widens the window by one fallback bucket: the y-scale
    // filters on bucket START, and a coarse bucket can SPAN a narrow viewport
    // while starting before it — without the slack it contributes nothing and
    // the refinement series would draw against a stale or default axis.
    const scale = calculateTimelineYScale(buckets, v, usableH, tickIntervals) ??
      (fb
        ? calculateTimelineYScale(fb.buckets, { startMs: v.startMs - (fb.bucketMs ?? 0), endMs: v.endMs }, usableH, tickIntervals)
        : null) ??
      this.lastScales.get(lane.symbolPath) ?? {
      minValue: 0,
      maxValue: 100,
      ticks: Array.from({ length: tickIntervals + 1 }, (_, i) => ({
        value: (100 * i) / tickIntervals,
        y: usableH - (i * usableH) / tickIntervals,
      })),
    };
    this.lastScales.set(lane.symbolPath, scale);
    const toY = (val: number): number =>
      topInset +
      (scale.maxValue > scale.minValue ? usableH - ((val - scale.minValue) / (scale.maxValue - scale.minValue)) * usableH : usableH / 2);

    ctx.save();
    ctx.translate(0, top);

    // Horizontal gridlines + y labels — this lane's own axis.
    ctx.strokeStyle = cBorder;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const tk of scale.ticks) {
      ctx.moveTo(0, topInset + tk.y);
      ctx.lineTo(pw, topInset + tk.y);
    }
    ctx.stroke();
    ctx.fillStyle = cLabel;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (const tk of scale.ticks) ctx.fillText(formatValue(tk.value), -8, topInset + tk.y);

    // Everything data-shaped clips to the lane strip.
    ctx.beginPath();
    ctx.rect(0, 0, pw, laneH);
    ctx.clip();

    // Pre-data wash: a flat dim fill left of the recorded-data boundary. Drawn
    // FIRST so any fallback/series pixels (there should be none) paint over it,
    // and deliberately solid — the hatched veil means "still loading", this
    // means "nothing was ever recorded here".
    if (this.dataStartMs !== null && this.dataStartMs > v.startMs) {
      const preX = Math.min(pw, timeToX(Math.min(this.dataStartMs, v.endMs), v, pw));
      if (preX > 0) {
        ctx.save();
        ctx.fillStyle = cLabel;
        ctx.globalAlpha = 0.05;
        ctx.fillRect(0, 0, preX, laneH);
        ctx.restore();
      }
    }

    // Series painter, shared by the active level and the stale fallback:
    // min/max band + avg line per contiguous segment (aggregated), or a plain
    // line with a held right edge (raw, publish-on-change semantics).
    const drawSeries = (
      sBuckets: readonly (typeof buckets)[number][],
      sBucketMs: number | null,
      sCovered: { startMs: number; endMs: number } | null,
      bandAlpha: number,
      lineAlpha: number,
    ): void => {
      if (sBuckets.length === 0) return;
      // Segment only the index window that can touch the viewport, padded by
      // two buckets per side: one because buckets draw at their CENTER (a
      // bucket starting before the viewport can still have its center inside)
      // and one so the polyline still enters from off-screen. The resident
      // array is 3–5× the visible set, and every off-screen vertex used to
      // cost a timeToX + lineTo per frame. For raw, the last resident sample
      // is always inside the window whenever the held extension needs it.
      const from = lowerBound(sBuckets, v.startMs) - 2;
      const to = lowerBound(sBuckets, v.endMs + 1) + 1;
      const segments = segmentBuckets(sBuckets, sBucketMs, from, to);
      const centerOff = sBucketMs === null ? 0 : sBucketMs / 2; // draw buckets at their center
      for (const [s, e] of segments) {
        // Raw is publish-on-change: the held extension below carries the last
        // sample's value to the end of the confirmed range, so a segment whose
        // samples all sit LEFT of the viewport still paints a held flatline
        // into view when `covered` reaches it — skip only when even that
        // extension ends before the viewport.
        const segEndMs =
          sBucketMs === null && sCovered
            ? Math.max(sBuckets[e].tsMs, sCovered.endMs)
            : sBuckets[e].tsMs + (sBucketMs ?? 0);
        if (segEndMs < v.startMs || sBuckets[s].tsMs > v.endMs) continue;
        if (s === e && sBucketMs !== null) {
          // A lone bucket: the band polygon below is zero-area and the avg
          // polyline a single point — neither paints, which reads as a
          // confirmed GAP (worse than the veil). Draw the bucket explicitly
          // across its full [ts, ts+bucket] extent.
          const b = sBuckets[s];
          const x0 = timeToX(b.tsMs, v, pw);
          const x1 = timeToX(b.tsMs + sBucketMs, v, pw);
          const yHi = toY(b.max);
          const yLo = toY(b.min);
          ctx.fillStyle = theme.line;
          ctx.globalAlpha = bandAlpha;
          ctx.fillRect(x0, Math.min(yHi, yLo), Math.max(1, x1 - x0), Math.max(1, Math.abs(yLo - yHi)));
          ctx.globalAlpha = lineAlpha;
          ctx.strokeStyle = theme.line;
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.moveTo(x0, toY(b.avg));
          ctx.lineTo(x1, toY(b.avg));
          ctx.stroke();
          ctx.globalAlpha = 1;
          continue;
        }
        // Path vertex with far-off-canvas clamping: a retained off-screen
        // sentinel can sit hours before a seconds-wide viewport, and path
        // coordinates in the millions lose float precision in the rasterizer
        // (wrong or missing slope at the edge). Replace such a point with its
        // linear interpolation toward its in-segment neighbor, evaluated one
        // plot-width past the edge — same slope through the viewport edge,
        // bounded coordinates. Only a segment's first/last point can be that
        // far out (interior points of a partially visible segment are near
        // the retain window, i.e. within a couple of plot-widths).
        const vertex = (i: number, value: (b: (typeof sBuckets)[number]) => number): void => {
          let x = timeToX(sBuckets[i].tsMs + centerOff, v, pw);
          let y = toY(value(sBuckets[i]));
          if (x < -pw || x > 2 * pw) {
            const xc = x < -pw ? -pw : 2 * pw;
            const nb = i === s ? Math.min(s + 1, e) : Math.max(e - 1, s);
            if (nb !== i) {
              const nx = timeToX(sBuckets[nb].tsMs + centerOff, v, pw);
              const ny = toY(value(sBuckets[nb]));
              if (nx !== x) y = ny + ((y - ny) * (xc - nx)) / (x - nx);
            }
            x = xc;
          }
          ctx.lineTo(x, y);
        };
        if (sBucketMs !== null) {
          ctx.beginPath();
          for (let i = s; i <= e; i++) vertex(i, (b) => b.max);
          for (let i = e; i >= s; i--) vertex(i, (b) => b.min);
          ctx.closePath();
          ctx.fillStyle = theme.line;
          ctx.globalAlpha = bandAlpha;
          ctx.fill();
          ctx.globalAlpha = 1;
        }
        ctx.beginPath();
        for (let i = s; i <= e; i++) vertex(i, (b) => b.avg);
        if (sBucketMs === null && sCovered) {
          // Raw is publish-on-change: the value provably HELD from the last
          // sample to the end of what the historian confirmed — draw it held.
          const held = Math.min(sCovered.endMs, v.endMs);
          if (held > sBuckets[e].tsMs) ctx.lineTo(timeToX(held, v, pw), toY(sBuckets[e].avg));
        }
        ctx.strokeStyle = theme.line;
        ctx.lineWidth = 1.6;
        ctx.lineJoin = 'round';
        ctx.globalAlpha = lineAlpha;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    };

    // Ranges of the viewport the ACTIVE level doesn't cover (≤2, since
    // `covered` is one contiguous interval). Where the FALLBACK covers them it
    // draws valid alternate-resolution history at full strength; only ranges
    // covered by NEITHER get the not-yet-fetched veil — the loading state,
    // deliberately distinct from a confirmed gap.
    const covered = lane.data?.covered ?? null;
    const rawBlind: Array<[number, number]> = [];
    if (!covered) rawBlind.push([v.startMs, v.endMs]);
    else {
      if (covered.startMs > v.startMs) rawBlind.push([v.startMs, covered.startMs]);
      if (covered.endMs < v.endMs) rawBlind.push([covered.endMs, v.endMs]);
    }
    // The pre-data region is KNOWN empty — never hatch it as "loading".
    const blind = clampBlindRanges(rawBlind, this.dataStartMs);
    const veil = (x0: number, x1: number): void => {
      const a = Math.max(0, x0);
      const b = Math.min(pw, x1);
      if (b - a < 1) return;
      ctx.save();
      ctx.strokeStyle = cLabel;
      ctx.globalAlpha = 0.07;
      ctx.beginPath();
      ctx.rect(a, 0, b - a, laneH);
      ctx.clip();
      ctx.beginPath();
      for (let x = a - laneH; x < b; x += 8) {
        ctx.moveTo(x, laneH);
        ctx.lineTo(x + laneH, 0);
      }
      ctx.stroke();
      ctx.restore();
    };
    for (const [t0, t1] of blind) {
      const f0 = fb ? Math.max(t0, fb.covered.startMs) : t1;
      const f1 = fb ? Math.min(t1, fb.covered.endMs) : t0;
      if (f1 - f0 < 1) {
        // Fallback doesn't reach into this range — all of it is loading.
        veil(timeToX(t0, v, pw), timeToX(t1, v, pw));
        continue;
      }
      // A resident coarser/finer level is valid history at a different
      // resolution, so keep it full-strength while the active rung refines.
      // Only a range covered by NO level gets the loading veil above.
      ctx.save();
      ctx.beginPath();
      ctx.rect(timeToX(f0, v, pw), 0, timeToX(f1, v, pw) - timeToX(f0, v, pw), laneH);
      ctx.clip();
      drawSeries(fb!.buckets, fb!.bucketMs, fb!.covered, 0.08, 1);
      ctx.restore();
      // ...and the slivers the fallback doesn't span still veil.
      if (f0 > t0) veil(timeToX(t0, v, pw), timeToX(f0, v, pw));
      if (f1 < t1) veil(timeToX(f1, v, pw), timeToX(t1, v, pw));
    }

    // The active level's series draws LAST, full-strength, over everything.
    drawSeries(buckets, lane.data?.bucketMs ?? null, covered, 0.1, 1);

    // Lane label (Perfetto-style overlay) — only when there's more than one
    // strip to tell apart; a single lane is already named by its chip.
    if (labelled) {
      ctx.fillStyle = theme.line;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.font = '11px "SF Mono", ui-monospace, monospace';
      ctx.fillText(lane.symbolPath, 6, 5);
      ctx.font = '10px "SF Mono", ui-monospace, monospace';
    }

    ctx.restore();
    return { scale, toY };
  }

  private drawCrosshair(
    ctx: CanvasRenderingContext2D,
    v: TimelineViewport,
    pw: number,
    ph: number,
    laneTop: (i: number) => number,
    lanesScale: LaneScale[],
    borderColor: string,
  ): void {
    if (this.hoverX === null || this.dragging || this.lanes.length === 0) {
      this.tooltip.hide();
      return;
    }
    const t = xToTime(this.hoverX, v, pw);
    const rect = this.canvasRect;

    // Each lane independently finds its nearest bucket and passes/fails the
    // snap-distance test — a lane in a gap (or the loading veil) contributes
    // no row rather than inventing a value.
    const rows: HoverReadoutRow[] = [];
    let timeText: string | null = null;
    for (let li = 0; li < this.lanes.length; li++) {
      const lane = this.lanes[li];
      const { toY } = lanesScale[li];
      const buckets = lane.data?.buckets ?? [];
      if (buckets.length === 0) continue;
      const bucketMs = lane.data?.bucketMs ?? null;
      const centerOff = bucketMs === null ? 0 : bucketMs / 2;
      // Nearest bucket by center time (binary search — buckets are sorted).
      let lo = 0;
      let hi = buckets.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if (buckets[mid].tsMs + centerOff < t) lo = mid + 1;
        else hi = mid;
      }
      const cand =
        lo > 0 && Math.abs(buckets[lo - 1].tsMs + centerOff - t) < Math.abs(buckets[lo].tsMs + centerOff - t) ? lo - 1 : lo;
      const b = buckets[cand];
      const bx = timeToX(b.tsMs + centerOff, v, pw);
      const maxSnapPx = bucketMs === null ? 40 : Math.max(24, (bucketMs / (v.endMs - v.startMs)) * pw * 1.5);
      if (Math.abs(bx - this.hoverX) > maxSnapPx) continue;

      const theme = CHART_THEMES[lane.theme] ?? CHART_THEMES.frost;
      const range = b.min !== b.max ? ` (${formatValue(b.min)}…${formatValue(b.max)})` : '';
      if (rect) {
        rows.push({
          dotClientX: rect.left + MARGIN.left + bx,
          dotClientY: rect.top + MARGIN.top + laneTop(li) + toY(b.avg),
          color: theme.line,
          valueText: `${formatValue(b.avg)}${range}`,
        });
      }
      // The lanes share one bucket grid (one rung for one viewport), so the
      // first in-range lane's bucket names the instant for the whole chip.
      if (timeText === null) timeText = formatCrosshairTime(b.tsMs + centerOff, v.endMs - v.startMs);
    }

    if (rows.length === 0 || timeText === null || !rect) {
      this.tooltip.hide();
      return;
    }

    ctx.strokeStyle = borderColor;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(this.hoverX, 0);
    ctx.lineTo(this.hoverX, ph);
    ctx.stroke();
    ctx.setLineDash([]);

    this.tooltip.show({
      cursorClientX: this.hoverClient.x,
      cursorClientY: this.hoverClient.y,
      timeText,
      rows,
    });
  }

  render() {
    return html`<canvas
      class="vtc-canvas"
      @pointerdown=${this.onPointerDown}
      @pointermove=${this.onPointerMove}
      @pointerup=${this.onPointerUp}
      @pointercancel=${this.onPointerUp}
      @pointerleave=${this.onPointerLeave}
    ></canvas>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-timeline-chart': VitesseTimelineChart;
  }
}
